import type React from 'react';

import { Typography } from '@/components/ui/typography';

import { DetailField } from './DetailField';
import { orderStateOptions } from '../../../../config/options.config';
import { useDropdownOptions } from '../../services/useDropdownOptions';
import { dropDownURLS } from '../../add-property/hooks/propertyFormUtils';
import { getEntityDropdownConfig } from '../../../../services/entity-tree';

import type { PropertyEntity } from '../../../../types/property.types';

interface Props {
  property: PropertyEntity;
}

export function PropertyDetailsTab({ property }: Props) {
  const propertyTypeOptions = useDropdownOptions(dropDownURLS.propertyTypeURL, 'property');
  const entityConfig = getEntityDropdownConfig();

  return (
    <div className="space-y-8">
      <Section title="Address">
        <Grid>
          <DetailField
            label="Property Type"
            name="PropertyType"
            type="select"
            options={propertyTypeOptions}
            value={property.propertyType}
            required
          />
          <DetailField
            label="Address"
            name="address"
            type="text"
            value={property.address1}
            maxLength={46}
            required
          />
          <DetailField
            label="Apt/Suite/Other"
            name="other"
            type="text"
            value={property.unitNumber}
            maxLength={46}
          />
          <DetailField
            label="City"
            name="city"
            type="text"
            value={property.city}
            maxLength={28}
            required
          />
          <DetailField
            label="State"
            name="state"
            type="combobox"
            options={orderStateOptions}
            value={property.state}
            required
          />
          <DetailField
            label="ZIP Code"
            name="zipCode"
            type="text"
            value={property.zipCode}
            maxLength={15}
            required
          />
          <DetailField
            label="Entity"
            name="entity"
            type="select"
            options={entityConfig.options}
            value={property.entity}
            locked /* Entity edits go through the org switcher, not here */
          />
          <DetailField
            label="Property ID"
            name="clientPropertyId"
            type="text"
            value={property.clientPropertyId}
            maxLength={255}
          />
        </Grid>
      </Section>

      <Section title="Physical">
        <Grid>
          <DetailField
            label="Bedrooms"
            name="bedrooms"
            type="number"
            value={property.bedrooms}
          />
          <DetailField
            label="Bathrooms"
            name="bathrooms"
            type="number"
            value={property.bathrooms}
            step={0.5}
          />
          {/* Formik field name: squareFoot; PropertyEntity field: squareFeet */}
          <DetailField
            label="Square Footage"
            name="squareFoot"
            type="text"
            value={property.squareFeet}
            maxLength={6}
          />
          <DetailField
            label="Unit Status"
            name="propertyStatus"
            type="text"
            value={property.propertyStatus}
            maxLength={50}
          />
        </Grid>
      </Section>

      <Section title="Categorization">
        <Grid>
          <DetailField
            label="Portfolio Name"
            name="portfolioName"
            type="text"
            value={property.portfolioName}
            maxLength={160}
          />
          {/* Formik field name: propertyGroup; PropertyEntity field: propertyGroupName */}
          <DetailField
            label="Property Group"
            name="propertyGroup"
            type="text"
            value={property.propertyGroupName}
            maxLength={160}
          />
          <DetailField
            label="Unit ID"
            name="clientUnitId"
            type="text"
            value={property.clientUnitId}
            maxLength={255}
          />
        </Grid>
      </Section>
    </div>
  );
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <Typography
      as="h3"
      className="text-sm font-semibold text-foreground border-b border-border pb-2 mb-4"
    >
      {title}
    </Typography>
    {children}
  </div>
);

const Grid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">{children}</div>
);
